import React, { useState, useEffect, useCallback } from 'react';
import { Box, Typography, Button, LinearProgress, Paper, Grid, Chip, IconButton } from '@mui/material';
import { PlayArrow, Refresh, Add } from '@mui/icons-material';
import { GlassContainer } from '../styles/sharedStyles';
import { useTranslation } from 'react-i18next';

const SERVER_NAMES = ['TrueNAS', 'Immich', 'Jellyfin', 'Home Assistant', 'Nextcloud', 'Pi-hole'];

const ServerPingDefender = () => {
  const { t } = useTranslation();
  const [gameStarted, setGameStarted] = useState(false);
  const [score, setScore] = useState(0);
  const [servers, setServers] = useState([]);
  const [gameOver, setGameOver] = useState(false);
  const [highScore, setHighScore] = useState(0);
  const [difficulty, setDifficulty] = useState(1);

  const initializeServers = useCallback(() => {
    return SERVER_NAMES.map((name, index) => ({
      id: index,
      name,
      health: 100,
      pingTime: Math.floor(Math.random() * 20) + 5,
      status: 'online'
    }));
  }, []);

  const startGame = () => {
    setServers(initializeServers());
    setScore(0);
    setGameOver(false);
    setGameStarted(true);
    setDifficulty(1);
  };

  const pingServer = (serverId) => {
    if (gameOver) return;
    
    setServers(prev => prev.map(server => {
      if (server.id === serverId && server.health > 0) {
        const healAmount = 15 + Math.floor(Math.random() * 10);
        return { ...server, health: Math.min(100, server.health + healAmount) };
      }
      return server;
    }));
    setScore(prev => prev + 10);
  };

  const addNewServer = () => {
    if (gameOver || servers.length >= 12) return;
    
    const newId = servers.length > 0 ? Math.max(...servers.map(s => s.id)) + 1 : 0;
    const newServer = {
      id: newId,
      name: `Server-${newId + 1}`,
      health: 50 + Math.floor(Math.random() * 30),
      pingTime: Math.floor(Math.random() * 15) + 10,
      status: 'online'
    };
    
    setServers(prev => [...prev, newServer]);
    setScore(prev => prev - 25);
  };

  useEffect(() => {
    if (!gameStarted || gameOver) return;

    const interval = setInterval(() => {
      setServers(prev => {
        const updated = prev.map(server => {
          const decayRate = 2 + (difficulty * 0.5);
          const newHealth = Math.max(0, server.health - decayRate);
          return { ...server, health: newHealth };
        });

        const allDead = updated.every(s => s.health <= 0);
        if (allDead) {
          setGameOver(true);
          if (score > highScore) {
            setHighScore(score);
            localStorage.setItem('pingDefenderHighScore', score);
          }
        }

        return updated;
      });
    }, 500);

    return () => clearInterval(interval);
  }, [gameStarted, gameOver, difficulty, score, highScore]);

  useEffect(() => {
    const savedHighScore = localStorage.getItem('pingDefenderHighScore');
    if (savedHighScore) {
      setHighScore(parseInt(savedHighScore, 10));
    }
  }, []);

  useEffect(() => {
    if (score > 0 && score % 100 === 0) {
      setDifficulty(prev => prev + 1);
    }
  }, [score]);

  const getHealthColor = (health) => {
    if (health > 60) return '#00ff88';
    if (health > 30) return '#ffaa00';
    return '#ff4444';
  };

  const getStatusEmoji = (health) => {
    if (health > 60) return '🟢';
    if (health > 30) return '🟡';
    return '🔴';
  };

  return (
    <Box>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3, flexWrap: 'wrap', gap: 2 }}>
        <Box>
          <Typography variant="h5" sx={{ fontWeight: 'bold', color: '#00ff88' }}>
            🎯 Server Ping Defender
          </Typography>
          <Typography variant="body2" sx={{ color: 'rgba(255,255,255,0.6)', mt: 0.5 }}>
            Click servers to ping them before they go offline!
          </Typography>
        </Box>
        <Box sx={{ display: 'flex', gap: 2, alignItems: 'center' }}>
          <Chip label={`${t('games.pingDefender.score')}: ${score}`} sx={{ bgcolor: 'rgba(0, 212, 255, 0.2)', color: '#00d4ff' }} />
          <Chip label={`${t('games.pingDefender.highScore')}: ${highScore}`} sx={{ bgcolor: 'rgba(255, 215, 0, 0.2)', color: '#ffd700' }} />
          <Chip label={`Level: ${difficulty}`} sx={{ bgcolor: 'rgba(255, 68, 68, 0.2)', color: '#ff4444' }} />
        </Box>
      </Box>

      {!gameStarted ? (
        <GlassContainer sx={{ textAlign: 'center', py: 8 }}>
          <Typography variant="h4" sx={{ mb: 2, color: '#00ff88' }}>
            🖥️ Keep Your Servers Alive!
          </Typography>
          <Typography variant="body1" sx={{ color: 'rgba(255,255,255,0.7)', mb: 4, maxWidth: 500, mx: 'auto' }}>
            Servers constantly lose connection. Click them to send pings and restore their health. 
            Don't let all servers go offline!
          </Typography>
          <Button 
            variant="contained" 
            size="large"
            startIcon={<PlayArrow />}
            onClick={startGame}
            sx={{ 
              bgcolor: 'linear-gradient(135deg, #00ff88 0%, #00cc6a 100%)',
              color: '#000',
              fontWeight: 'bold',
              px: 4,
              py: 1.5,
              fontSize: '1.1rem',
              '&:hover': {
                bgcolor: 'linear-gradient(135deg, #00ff88 0%, #00ff88 100%)',
                boxShadow: '0 0 20px rgba(0, 255, 136, 0.5)'
              }
            }}
          >
            {t('games.pingDefender.startGame')}
          </Button>
        </GlassContainer>
      ) : gameOver ? (
        <GlassContainer sx={{ textAlign: 'center', py: 8 }}>
          <Typography variant="h3" sx={{ mb: 2, color: '#ff4444' }}>
            💀 {t('games.pingDefender.gameOver')}
          </Typography>
          <Typography variant="h4" sx={{ mb: 4, color: '#00ff88' }}>
            {t('games.pingDefender.score')}: {score}
          </Typography>
          {score >= highScore && score > 0 && (
            <Typography variant="h5" sx={{ mb: 2, color: '#ffd700' }}>
              🏆 {t('games.pingDefender.newHighScore')}
            </Typography>
          )}
          <Box sx={{ display: 'flex', gap: 2, justifyContent: 'center', mt: 4 }}>
            <Button 
              variant="contained" 
              size="large"
              startIcon={<Refresh />}
              onClick={startGame}
              sx={{ 
                bgcolor: 'linear-gradient(135deg, #00ff88 0%, #00cc6a 100%)',
                color: '#000',
                fontWeight: 'bold',
                px: 4,
                '&:hover': {
                  boxShadow: '0 0 20px rgba(0, 255, 136, 0.5)'
                }
              }}
            >
              Play Again
            </Button>
          </Box>
        </GlassContainer>
      ) : (
        <>
          <Box sx={{ mb: 3 }}>
            <Button 
              variant="outlined"
              size="small"
              startIcon={<Add />}
              onClick={addNewServer}
              disabled={servers.length >= 12}
              sx={{ 
                borderColor: 'rgba(0, 212, 255, 0.5)',
                color: '#00d4ff',
                '&:hover': { borderColor: '#00d4ff', bgcolor: 'rgba(0, 212, 255, 0.1)' }
              }}
            >
              Add Server (-25 pts)
            </Button>
          </Box>
          
          <Grid container spacing={2}>
            {servers.map((server) => (
              <Grid item xs={12} sm={6} md={4} key={server.id}>
                <Paper
                  onClick={() => pingServer(server.id)}
                  sx={{
                    p: 2,
                    cursor: 'pointer',
                    bgcolor: 'rgba(255, 255, 255, 0.05)',
                    border: `2px solid ${getHealthColor(server.health)}`,
                    borderRadius: '12px',
                    transition: 'all 0.2s ease',
                    '&:hover': {
                      bgcolor: 'rgba(255, 255, 255, 0.1)',
                      transform: 'scale(1.02)',
                      boxShadow: `0 0 20px ${getHealthColor(server.health)}40`
                    },
                    '&:active': {
                      transform: 'scale(0.98)'
                    }
                  }}
                >
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
                    <Typography variant="subtitle1" sx={{ fontWeight: 'bold', color: '#fff' }}>
                      {getStatusEmoji(server.health)} {server.name}
                    </Typography>
                    <Typography variant="caption" sx={{ color: 'rgba(255,255,255,0.5)' }}>
                      {server.pingTime}ms
                    </Typography>
                  </Box>
                  <LinearProgress 
                    variant="determinate" 
                    value={server.health}
                    sx={{ 
                      height: 8,
                      borderRadius: 4,
                      bgcolor: 'rgba(255,255,255,0.1)',
                      '& .MuiLinearProgress-bar': {
                        bgcolor: getHealthColor(server.health),
                        borderRadius: 4
                      }
                    }}
                  />
                  <Typography variant="caption" sx={{ color: getHealthColor(server.health), mt: 0.5, display: 'block' }}>
                    {server.health}% health
                  </Typography>
                </Paper>
              </Grid>
            ))}
          </Grid>
        </>
      )}
    </Box>
  );
};

export default ServerPingDefender;
